import { createContext, useContext, type ReactNode } from 'react';

import { DEFAULT_ACCESSIBILITY_PREFERENCES } from './types';
import type { AccessibilityPreferences } from './types';

export interface AccessibilityPreferencesContextValue {
  preferences: AccessibilityPreferences;
  setPreference: <K extends keyof AccessibilityPreferences>(key: K, value: AccessibilityPreferences[K]) => void;
  reset: () => void;
}

// Same shape as site-org-context.tsx (createContext + tiny provider + one
// hook) — provided once per published site (PublicSite.tsx), which owns
// the single useAccessibilityPreferences() call so the same state instance
// both feeds this context (for the panel) and the effective theme (for
// rendering) rather than the two drifting apart as separate hook copies.
const AccessibilityPreferencesContext = createContext<AccessibilityPreferencesContextValue>({
  preferences: DEFAULT_ACCESSIBILITY_PREFERENCES,
  setPreference: () => {},
  reset: () => {},
});

export function AccessibilityPreferencesStateProvider({
  value,
  children,
}: {
  value: AccessibilityPreferencesContextValue;
  children: ReactNode;
}) {
  return <AccessibilityPreferencesContext.Provider value={value}>{children}</AccessibilityPreferencesContext.Provider>;
}

export function useAccessibilityPreferencesContext(): AccessibilityPreferencesContextValue {
  return useContext(AccessibilityPreferencesContext);
}

// Thin selector, not a separate subsystem — there are currently no
// carousels/auto-playing widgets in the block registry to pause. This
// exists so the panel's "Pause moving content" toggle has somewhere real
// to write, and so the first animated block built later has an immediate,
// already-wired way to read visitor intent instead of inventing its own.
export function usePauseAnimations(): boolean {
  return useAccessibilityPreferencesContext().preferences.pauseAnimations;
}
